import {
  Field,
  FileField,
  ImageField,
  LinkField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import {
  DynamicHeading,
  mapSharedPropertiesToFields,
  SitecoreFieldTypes,
} from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import VideoBackground from 'composables/Banners/VideoBanner/VideoBackground';
import { VideoBannerProducts } from 'composables/Banners/VideoBanner/VideoBannerProducts';
import { ProductItem } from 'composables/Banners/VideoBanner/VideoBannerProducts';
import NavButton from 'composables/shared/NavButton/NavButton';
import { ComponentProps } from 'lib/component-props';

type VideoBannerFields = {
  videoLg: FileField;
  videoMd: FileField;
  videoXs: FileField;
  image: ImageField;
  title: Field<string>;
  titleSmall: Field<string>;
  eyebrowTitle: Field<string>;
  titleFontSizeStyle: Field<string>;
  titleSmallFontSizeStyle: Field<string>;
  primaryButtonText: Field<string>;
  primaryButtonLink: LinkField;
  secondaryButtonText: Field<string>;
  secondaryButtonLink: LinkField;
  backgroundContrast: Field<number>;
  isMainHeading: Field<boolean>;
  withProductsSection: Field<boolean>;
  productsList: ProductItem[];
} & VideoBannerProducts;

type VideoBannerProps = ComponentProps & {
  fields: VideoBannerFields;
};

const VideoBanner = ({ fields }: VideoBannerProps) => {
  const {
    videoLg,
    videoMd,
    videoXs,
    image,
    title,
    titleSmall,
    eyebrowTitle,
    titleFontSizeStyle,
    titleSmallFontSizeStyle,
    backgroundContrast,
    primaryButtonLink,
    primaryButtonText,
    secondaryButtonLink,
    secondaryButtonText,
    withProductsSection,
    productsList,
    explanatoryText,
    explanatoryBackgroundColor,
    isMainHeading,
  } = mapSharedPropertiesToFields<VideoBannerFields>(fields as SitecoreFieldTypes);

  const shouldRenderButtons =
    (!!primaryButtonLink?.value?.href && !!primaryButtonText?.value) ||
    (!!secondaryButtonLink?.value?.href && !!secondaryButtonText?.value);
  return (
    <VideoBackground
      videoLg={videoLg}
      videoMd={videoMd}
      videoXs={videoXs}
      image={image}
      videoContainerClass="min-h-screen flex"
      backgroundContrast={backgroundContrast.value}
      reducedWidth={false}
      hideControlsOnMobile={true}
    >
      <div
        className={classNames(
          'container flex min-h-full max-w-6xl flex-col items-center justify-center overflow-hidden',
          withProductsSection?.value && 'pb-80 pt-24'
        )}
      >
        {!!eyebrowTitle?.value && (
          <Eyebrow text={eyebrowTitle.value as string} align="center" className="text-white" />
        )}

        {!!title?.value && (
          <DynamicHeading
            className="mt-5 text-center text-[2.25rem] font-bold leading-[110%] text-white md:text-[5rem]"
            style={{ fontSize: titleFontSizeStyle?.value }}
            isMainHeading={isMainHeading?.value}
          >
            <span dangerouslySetInnerHTML={{ __html: sanitize(title.value as string) }} />
          </DynamicHeading>
        )}

        {!!titleSmall?.value && (
          <span
            className="mt-5 block text-center text-2xl font-normal leading-[110%] text-white md:text-[2.25rem]"
            style={{ fontSize: titleSmallFontSizeStyle?.value }}
          >
            <span dangerouslySetInnerHTML={{ __html: sanitize(titleSmall.value as string) }} />
          </span>
        )}

        {shouldRenderButtons && (
          <div className="dark mt-[30px] flex flex-wrap justify-center gap-6">
            {!!primaryButtonText?.value && (
              <NavButton
                variant="primary"
                size="m"
                link={primaryButtonLink}
                title={primaryButtonText?.value}
              />
            )}
            {!!secondaryButtonText?.value && (
              <NavButton
                variant="secondary"
                size="m"
                link={secondaryButtonLink}
                title={secondaryButtonText?.value}
              />
            )}
          </div>
        )}
      </div>
      <VideoBannerProducts
        withProductsSection={withProductsSection}
        explanatoryBackgroundColor={explanatoryBackgroundColor}
        explanatoryText={explanatoryText}
        productsList={productsList}
      />
    </VideoBackground>
  );
};

export default withDatasourceCheck()<VideoBannerProps>(VideoBanner);
